import { format } from 'date-fns';
import { 
  Calendar, 
  Clock, 
  MapPin,
  Gift,
  AlertCircle,
  Package,
  Star
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatAddress, formatSimplifiedTimeSlots } from '../../utils/subscriptionUtils';
import { useState, useEffect } from 'react';
import AddressManagementModal from '../AddressManagementModal';
import { Address } from '../../types/api';
import { useTranslation } from 'react-i18next';
import { formatDateLocale } from '../../utils/dateUtilsLocale';

interface SubscriptionDetailsProps {
  subscription: {
    id?: string;
    plan?: string | null;
    status?: string | null;
    nextDeliveryDate?: string | null;
    dayOfWeek?: number;
    address?: any;
    isGift?: boolean;
    giftType?: 'direct' | 'code';
    giftSenderName?: string;
    giftRecipientName?: string;
    deliverySettings?: {
      day_of_week?: number | string;
      time_slots?: string[];
    };
    endDate?: string | null;
    isRecurring?: boolean;
    paymentType?: string;
    remaining_deliveries?: number;
    original_deliveries?: number;
    quantity?: number;
    isTrial?: boolean;
  };
  onAddressUpdate?: (address: Address) => void;
}

const SubscriptionDetails: React.FC<SubscriptionDetailsProps> = ({ 
  subscription,
  onAddressUpdate
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // State for address modal
  const [showAddressModal, setShowAddressModal] = useState(false);
  
  // Using useEffect to log when modal state changes
  useEffect(() => {
    console.log(`SubscriptionDetails: Address modal state changed to: ${showAddressModal ? 'open' : 'closed'}`);
  }, [showAddressModal]);
  
  // Debug log for subscription data
  console.log('SubscriptionDetails data:', {
    address: subscription.address,
    deliverySettings: subscription.deliverySettings,
    dayOfWeek: subscription.dayOfWeek,
    nextDeliveryDate: subscription.nextDeliveryDate,
    status: subscription.status,
    id: subscription.id,
    paymentType: subscription.paymentType,
    remaining_deliveries: subscription.remaining_deliveries
  });
  
  // Convert day number to day name
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  // Get dayOfWeek from either direct prop or schedule
  const dayOfWeekValue = subscription.dayOfWeek !== undefined ? 
    subscription.dayOfWeek : 
    (subscription.deliverySettings?.day_of_week !== undefined ? 
      (typeof subscription.deliverySettings.day_of_week === 'string' ? 
        parseInt(subscription.deliverySettings.day_of_week as string) : 
        subscription.deliverySettings.day_of_week) : 
      undefined);
      
  const dayName = dayOfWeekValue !== undefined ? dayNames[dayOfWeekValue] : '';
  
  // Get timeSlot from either direct prop or schedule
  const timeSlots = subscription.deliverySettings?.time_slots || [];
  const formattedTimeSlots = formatSimplifiedTimeSlots(timeSlots);

  // Format address for display using the utility function
  const formattedAddress = subscription.address ? formatAddress(subscription.address) : t('subscription.details.noAddressSet');
    
  const getStatusBadgeClass = (status: string | null | undefined) => {
    switch(status) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'paused':
        return 'bg-yellow-100 text-yellow-700';
      case 'pending':
        return 'bg-blue-100 text-blue-800';
      case 'pending_renewal':
        return 'bg-orange-100 text-orange-700';
      case 'past_due':
        return 'bg-red-100 text-red-700';
      case 'cancelled':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status: string | null | undefined) => {
    switch(status) {
      case 'active':
        return t('subscription.details.status.active');
      case 'paused':
        return t('subscription.details.status.paused');
      case 'pending':
        return t('subscription.details.status.pending');
      case 'pending_renewal':
        return t('subscription.details.status.pending_renewal');
      case 'past_due':
        return t('subscription.details.status.past_due');
      case 'cancelled':
        return t('subscription.details.status.cancelled');
      default:
        return status || t('subscription.details.status.unknown');
    }
  };

  // Determine the effective status to display
  const getEffectiveStatus = () => {
    // Check if subscription should show as pending renewal
    // Handle both field name formats for compatibility
    const remainingCount = subscription.remaining_deliveries || (subscription as any).remainingDeliveries;
    
    if (subscription.status === 'active' && 
        subscription.isRecurring === false && 
        remainingCount === 0) {
      return 'pending_renewal';
    }
    return subscription.status;
  };

  const effectiveStatus = getEffectiveStatus();

  // Safe handler for closing the modal
  const handleCloseAddressModal = () => {
    console.log('Closing address modal from SubscriptionDetails');
    setShowAddressModal(false);
  };
  
  // Handler for address selection from modal
  const handleAddressSelected = (address: Address) => {
    console.log('Address selected in SubscriptionDetails:', address);
    
    // Close modal first
    setShowAddressModal(false);
    
    // Then update address via parent component if handler exists
    if (onAddressUpdate) {
      console.log('Calling onAddressUpdate with selected address');
      onAddressUpdate(address);
    }
  };

  // Function to open address modal
  const openAddressModal = () => {
    console.log('Opening address modal from SubscriptionDetails - BEFORE STATE CHANGE');
    setShowAddressModal(true);
    console.log('showAddressModal set to true');
  };
  


  return (
    <div className="bg-white rounded-2xl p-4 m-4 shadow">
      <div className="flex items-top justify-between mb-6">
        <div className="flex items-center">
          {subscription.isGift && (
            <Gift className="w-5 h-5 mr-2 text-amber-500" />
          )}
          {subscription.isTrial && (
            <Star className="w-5 h-5 mr-2 text-blue-500" />
          )}
          <div>
            <h2 className="text-lg font-semibold">
              {subscription.plan === 'premium' ? t('subscription.details.premium') : t('subscription.details.standard')} {t('subscription.details.plan')}
              {subscription.isTrial && (
                <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md font-medium">
                  {t('subscription.details.trial')}
                </span>
              )}
              {subscription.isGift && t('subscription.details.gift')}
              {subscription.quantity && subscription.quantity > 1 && ` × ${subscription.quantity}`}
            </h2>
            {subscription.isTrial && (
              <p className="text-sm text-blue-600 mt-1">
                {t('subscription.details.trialDescription')}
              </p>
            )}
            {subscription.isGift && (
              <p className="text-sm text-gray-600 mt-1">
                {subscription.giftSenderName ? 
                  `${t('subscription.details.receivedFrom')} ${subscription.giftSenderName}` : 
                  subscription.giftRecipientName ? 
                  `${t('subscription.details.sentTo')} ${subscription.giftRecipientName}` : 
                  subscription.giftType === 'direct' ? t('subscription.details.directlySentGift') : 
                  subscription.giftType === 'code' ? t('subscription.details.giftCode') : t('subscription.details.giftSubscription')}
              </p>
            )}
          </div>
        </div>
        <div className={`px-3 py-1 h-fit rounded-lg ${getStatusBadgeClass(effectiveStatus)}`}>
          <span className="text-sm font-medium">
            {getStatusLabel(effectiveStatus)}
          </span>
        </div>
      </div>
      
      {subscription.nextDeliveryDate && (
        <div className="">
          <div className="flex flex-col gap-1 text-sm">
            <span className='flex items-center font-medium'><Calendar className="w-4 h-4 mr-1" /> {t('subscription.details.nextDelivery')}</span>
            <span className=''>{formatDateLocale(new Date(subscription.nextDeliveryDate), 'EEEE, MMMM d, yyyy')}</span>
          </div>
          {dayName && formattedTimeSlots && (
            <div className="flex items-center text-sm text-gray-600">
              <Clock className="w-4 h-4 mr-1.5" />
              <span>{t('subscription.details.every')} {dayName} • {formattedTimeSlots}</span>
            </div>
          )}
          {subscription.nextDeliveryDate && dayOfWeekValue !== undefined && new Date(subscription.nextDeliveryDate).getDay() !== dayOfWeekValue && (
            <div className="text-xs text-amber-600 italic mt-1 pl-6">
              {t('subscription.details.scheduleNote')}
            </div>
          )}
        </div>
      )}
      
      {/* Remaining Deliveries Section for One-time Subscriptions */}
      {subscription.isRecurring === false && (
        (() => {
          // Handle both field name formats for compatibility
          const remainingCount = subscription.remaining_deliveries || (subscription as any).remainingDeliveries;
          const originalCount = subscription.original_deliveries || (subscription as any).originalDeliveries;
          
          if (remainingCount !== undefined) {
            return (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="flex flex-col gap-1 text-sm">
                  <span className='flex items-center font-medium'>
                    <Package className="w-4 h-4 mr-1" /> 
                    {t('subscription.details.remainingDeliveries')}
                  </span>
                  <div className="flex items-center">
                    <span className="text-lg font-semibold">{remainingCount}</span>
                    {originalCount && (
                      <span className="text-xs text-gray-500 ml-2">
                        ({t('subscription.details.originalCount')}: {originalCount})
                      </span>
                    )}
                  </div>
                  {remainingCount <= 2 && effectiveStatus === 'active' && (
                    <span className="text-xs text-amber-600 font-medium mt-1">
                      {remainingCount === 1 ? 
                        t('subscription.details.lastDeliveryWarning') : 
                        t('subscription.details.lowDeliveriesWarning')}
                    </span>
                  )}
                </div>
              </div>
            );
          }
          return null;
        })()
      )}
      
      {/* Subscription End Date / Next Renewal Section */}
      {subscription.endDate && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex flex-col gap-1 text-sm">
            <span className='flex items-center font-medium'>
              <AlertCircle className="w-4 h-4 mr-1" /> 
              {subscription.isRecurring ? t('subscription.details.nextRenewalDate') : t('subscription.details.subscriptionEndDate')}
            </span>
            <span className={`${effectiveStatus === 'paused' ? 'text-gray-400' : ''}`}>
              {formatDateLocale(new Date(subscription.endDate), 'MMMM d, yyyy')}
            </span>
            {effectiveStatus === 'paused' && (
              <span className="text-xs text-gray-500 italic mt-1">
                {t('subscription.details.pauseNote')}
              </span>
            )}
          </div>
        </div>
      )}
      
      {subscription.address && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className='flex items-center text-sm justify-between pb-1'>
            <span className='flex items-center font-medium'> <MapPin className="w-4 h-4 mr-1" /> {t('subscription.details.deliverAddress')} </span>
            <button 
              onClick={openAddressModal}
              className="text-xs text-gray-400 font-normal bg-white p-0 border-none underline-offset-4 underline"
            >
              {t('subscription.details.change')}
            </button>
          </div>
          <span className='text-sm pl-1'>{formattedAddress}</span>
          {subscription.address.addressLabel && (
            <div className="mt-1 pl-1">
              <span className={`inline-block text-xs px-2 py-1 rounded ${
                subscription.address.addressLabel === 'HOME' ? 'bg-green-100 text-green-800' :
                subscription.address.addressLabel === 'OFFICE' ? 'bg-gray-100 text-gray-800' :
                subscription.address.addressLabel === 'SHOP' ? 'bg-blue-100 text-blue-800' :
                'bg-purple-100 text-purple-800'
              }`}>
                {subscription.address.addressLabel}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Only render the modal when it's needed */}
      {showAddressModal && (
        <AddressManagementModal
          isOpen={showAddressModal}
          onClose={handleCloseAddressModal}
          onAddressSelect={handleAddressSelected}
          selectedAddress={subscription.address}
          isSubscription={true}
        />
      )}
    </div>
  );
};

export default SubscriptionDetails; 